import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { analytics } from '@/utils/analytics';

export const usePageTracking = () => {
  const location = useLocation();
  const prevLocationRef = useRef<string>('');

  useEffect(() => {
    if (location.pathname !== prevLocationRef.current) {
      analytics.trackPageView(location.pathname, document.title);
      prevLocationRef.current = location.pathname;
    }
  }, [location]);
};

export const useScrollTracking = () => {
  const location = useLocation();
  const lastScrollPercentage = useRef<number>(0);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const documentHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight;
      const scrollPercentage = Math.round((scrollTop / documentHeight) * 100);

      // Track every 25% milestone
      if (scrollPercentage >= lastScrollPercentage.current + 25) {
        const milestone = Math.floor(scrollPercentage / 25) * 25;
        analytics.trackScrollDepth(milestone, location.pathname);
        lastScrollPercentage.current = milestone;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [location.pathname]);

  // Reset scroll tracking on route change
  useEffect(() => {
    lastScrollPercentage.current = 0;
  }, [location.pathname]);
};

export const useTimeOnPage = () => {
  const location = useLocation();
  const startTimeRef = useRef<number>(Date.now());

  useEffect(() => {
    startTimeRef.current = Date.now();

    const handleBeforeUnload = () => {
      const timeSpent = Math.round((Date.now() - startTimeRef.current) / 1000);
      if (timeSpent > 5) { // Only track if user spent more than 5 seconds
        analytics.trackTimeOnPage(timeSpent, location.pathname);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      // Track time when component unmounts (route change)
      const timeSpent = Math.round((Date.now() - startTimeRef.current) / 1000);
      if (timeSpent > 5) {
        analytics.trackTimeOnPage(timeSpent, location.pathname);
      }
    };
  }, [location.pathname]);
};

export const useFormTracking = (formName: string, formLocation: string) => {
  const trackFormStart = () => {
    analytics.trackEvent({
      event_name: 'form_start',
      event_category: 'form_interaction',
      event_label: formName,
      custom_parameters: {
        form_location: formLocation
      }
    });
  };

  const trackFormSubmit = (success: boolean, errorMessage?: string) => {
    analytics.trackFormSubmission({
      form_name: formName,
      form_location: formLocation,
      success,
      error_message: errorMessage
    });
  };

  const trackFieldInteraction = (fieldName: string, action: 'focus' | 'blur' | 'change') => {
    analytics.trackEvent({
      event_name: `form_field_${action}`,
      event_category: 'form_interaction',
      event_label: `${formName}_${fieldName}`,
      custom_parameters: {
        form_name: formName,
        field_name: fieldName,
        form_location: formLocation
      }
    });
  };

  return {
    trackFormStart,
    trackFormSubmit,
    trackFieldInteraction
  };
};

// Hook for tracking specific conversions
export const useConversionTracking = () => {
  const trackQuoteRequest = (data: {
    source: string;
    serviceType?: string;
    estimatedValue?: number;
    contactInfo?: { email?: string; phone?: string; };
  }) => {
    analytics.trackLead({
      source: data.source,
      type: 'quote_request',
      value: data.estimatedValue || 500, // Average quote value
      email: data.contactInfo?.email,
      phone: data.contactInfo?.phone,
      service_interest: data.serviceType
    });
  };

  const trackROICalculatorUsage = (result: {
    estimatedSavings: number;
    industry: string;
    propertySize: string;
  }) => {
    analytics.trackLead({
      source: 'roi_calculator',
      type: 'roi_calculator',
      value: Math.min(result.estimatedSavings * 0.1, 1000), // 10% of savings, capped at $1000
      service_interest: result.industry
    });

    analytics.trackEvent({
      event_name: 'roi_calculator_complete',
      event_category: 'tool_usage',
      event_label: result.industry,
      value: result.estimatedSavings,
      custom_parameters: {
        estimated_savings: result.estimatedSavings,
        industry: result.industry,
        property_size: result.propertySize
      }
    });
  };

  const trackNewsletterSignup = (email: string, source: string) => {
    analytics.trackLead({
      source,
      type: 'newsletter',
      value: 50, // Newsletter signup value
      email
    });
  };

  const trackContactFormSubmission = (data: {
    email?: string;
    phone?: string;
    service?: string;
    message?: string;
    source?: string;
  }) => {
    analytics.trackLead({
      source: data.source || 'contact_form',
      type: 'contact_form',
      value: 300, // Contact form lead value
      email: data.email,
      phone: data.phone,
      service_interest: data.service
    });
  };

  const trackPhoneCall = (source: string) => {
    analytics.trackLead({
      source,
      type: 'phone_call',
      value: 400 // Phone call lead value (typically higher intent)
    });
  };

  return {
    trackQuoteRequest,
    trackROICalculatorUsage,
    trackNewsletterSignup,
    trackContactFormSubmission,
    trackPhoneCall
  };
};

// Hook for e-commerce/booking tracking (future use)
export const useBookingTracking = () => {
  const trackBookingStart = (serviceType: string, estimatedValue: number) => {
    analytics.trackEvent({
      event_name: 'begin_checkout',
      event_category: 'booking',
      event_label: serviceType,
      value: estimatedValue,
      custom_parameters: {
        service_type: serviceType,
        booking_stage: 'started'
      }
    });
  };

  const trackBookingComplete = (bookingData: {
    bookingId: string;
    serviceType: string;
    value: number;
    date: string;
    customerInfo: {
      email: string;
      phone?: string;
    };
  }) => {
    analytics.trackPurchase({
      transaction_id: bookingData.bookingId,
      value: bookingData.value,
      currency: 'USD',
      items: [{
        item_id: bookingData.serviceType,
        item_name: `3D Scanning Service - ${bookingData.serviceType}`,
        category: 'service',
        quantity: 1,
        price: bookingData.value
      }]
    });

    analytics.trackLead({
      source: 'booking_system',
      type: 'quote_request',
      value: bookingData.value,
      email: bookingData.customerInfo.email,
      phone: bookingData.customerInfo.phone,
      service_interest: bookingData.serviceType
    });
  };

  return {
    trackBookingStart,
    trackBookingComplete
  };
};